import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import Header from '../components/Header';
import SignaturePad from '../components/SignaturePad';
import api from '../api/axios';

const Register = () => {
    const { register, login } = useAuth();
    const { config, loading: configLoading } = useConfig();
    const [userData, setUserData] = useState({
        nombre_1: '', nombre_2: '', apellido_1: '', apellido_2: '', email: '', identification: '', city: '', country: 'Colombia', representative: '', signature: null
    });
    const [recomendados, setRecomendados] = useState([]);
    const [signatureAccepted, setSignatureAccepted] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [eventStarted, setEventStarted] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const checkEventStatus = async () => {
            try {
                const res = await api.get('/agenda?status=active');
                if (res.data && res.data.length > 0) {
                    const sorted = [...res.data].sort((a, b) => {
                        const dateA = new Date(`${a.date.split('T')[0]}T${a.start_time}`);
                        const dateB = new Date(`${b.date.split('T')[0]}T${b.start_time}`);
                        return dateA - dateB;
                    });
                    const earliest = new Date(`${sorted[0].date.split('T')[0]}T${sorted[0].start_time}`);
                    const now = new Date();
                    if (now >= earliest) {
                        setEventStarted(true);
                    }
                }
            } catch (err) {
                console.error("Error checking event status", err);
            }
        };
        const fetchRecomendados = async () => {
            try {
                const res = await api.get('/recomendados');
                if (res.data) setRecomendados(res.data);
            } catch (err) {
                console.error("Error fetching recomendados", err);
            }
        };
        checkEventStatus();
        fetchRecomendados();
    }, []);

    const isCoherentWord = (str) => {
        if (!str || str.trim().length === 0) return false;
        // Permitir solo letras, espacios, guiones, tildes y eñes
        const regex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s'-]+$/;
        if (!regex.test(str)) return false;
        // Prevent 3 consecutive identical letters (e.g. "aaa", "bbb")
        if (/(.)\1\1/i.test(str)) return false;
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        if (reg.identification !== false) {
            if (!/^[0-9]+$/.test(userData.identification)) {
                setError('La identificación debe contener solo números.');
                setIsLoading(false);
                return;
            }
        }

        if (reg.terms_accepted && !termsAccepted) {
            setError('Debe aceptar los términos y condiciones para continuar.');
            setIsLoading(false);
            return;
        }

        if (reg.name !== false && (!isCoherentWord(userData.nombre_1) || (userData.nombre_2 && !isCoherentWord(userData.nombre_2)))) {
            setError('Uno de los nombres ingresados no parece válido.');
            setIsLoading(false);
            return;
        }

        if (reg.last_name !== false && (!isCoherentWord(userData.apellido_1) || (userData.apellido_2 && !isCoherentWord(userData.apellido_2)))) {
            setError('Uno de los apellidos ingresados no parece válido.');
            setIsLoading(false);
            return;
        }

        // Map identification to email if disabled
        const finalData = { ...userData };
        if (config?.registration_fields?.identification === false) {
            finalData.identification = userData.email;
        }

        const result = await register(finalData);
        if (result.success) {
            await login(finalData.identification);
            navigate('/');
        } else {
            setError(result.message);
            setIsLoading(false);
        }
    };

    if (configLoading) {
        return (
            <div className="min-h-screen bg-[#1c0042] flex items-center justify-center">
                <div className="w-10 h-10 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
            </div>
        );
    }

    const reg = config?.registration_fields || {};
    const pageDesign = config?.pageDesigns?.register || {};

    // Helper to determine if a field should be full width
    const getColSpan = (field, partner) => {
        if (reg[partner] === false) return "col-span-2";
        return "col-span-1";
    };

    const backgroundStyle = pageDesign.bgImage
        ? { backgroundImage: `url(${pageDesign.bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
        : {};

    const defaultBgClass = pageDesign.bgImage
        ? "min-h-screen flex flex-col items-center justify-center p-6 sm:p-10 animate-fadeIn overflow-y-auto relative"
        : "min-h-screen flex flex-col items-center justify-center p-6 sm:p-10 animate-fadeIn overflow-y-auto relative bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-[#1e1b4b] via-[#31105e] to-[#0f172a]";

    return (
        <div className={defaultBgClass} style={backgroundStyle}>
            {/* Dispersion/Glow Effect if no image */}
            {!pageDesign.bgImage && (
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute -top-1/4 -left-1/4 w-1/2 h-1/2 bg-blue-600/20 blur-[120px] rounded-full"></div>
                    <div className="absolute bottom-1/4 -right-1/4 w-1/2 h-1/2 bg-purple-600/20 blur-[120px] rounded-full"></div>
                </div>
            )}
            <Header title="Registro" subtitle="" />

            <div className="w-full max-w-sm z-10 space-y-8 bg-white/5 backdrop-blur-xl border border-white/10 p-8 rounded-[40px] shadow-2xl">


                <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
                    {/* Identification */}
                    {reg.identification !== false && (
                        <div className="col-span-2 space-y-1">
                            <label className="text-[8px] uppercase font-black tracking-[0.3em] text-white/40 pl-2">Identificación</label>
                            <input
                                className="w-full bg-white/5 border border-white/10 p-3 rounded-2xl focus:outline-none focus:ring-4 focus:ring-purple-500/20 transition-all font-bold text-white shadow-inner"
                                placeholder="Cédula"
                                value={userData.identification}
                                onChange={(e) => setUserData({ ...userData, identification: e.target.value.replace(/\D/g, '') })}
                                required
                            />
                        </div>
                    )}

                    {/* Name 1 and 2 */}
                    {reg.name !== false && (
                        <>
                            <div className="col-span-1 space-y-1">
                                <label className="text-[8px] uppercase font-black tracking-[0.3em] text-white/40 pl-2">Nombre 1</label>
                                <input
                                    className="w-full bg-white/5 border border-white/10 p-3 rounded-2xl focus:outline-none focus:ring-4 focus:ring-purple-500/20 transition-all font-bold text-white shadow-inner"
                                    value={userData.nombre_1}
                                    onChange={(e) => setUserData({ ...userData, nombre_1: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="col-span-1 space-y-1">
                                <label className="text-[8px] uppercase font-black tracking-[0.3em] text-white/40 pl-2">Nombre 2</label>
                                <input
                                    className="w-full bg-white/5 border border-white/10 p-3 rounded-2xl focus:outline-none focus:ring-4 focus:ring-purple-500/20 transition-all font-bold text-white shadow-inner"
                                    value={userData.nombre_2}
                                    onChange={(e) => setUserData({ ...userData, nombre_2: e.target.value })}
                                />
                            </div>
                        </>
                    )}

                    {/* Last Name 1 and 2 */}
                    {reg.last_name !== false && (
                        <>
                            <div className="col-span-1 space-y-1">
                                <label className="text-[8px] uppercase font-black tracking-[0.3em] text-white/40 pl-2">Apellido 1</label>
                                <input
                                    className="w-full bg-white/5 border border-white/10 p-3 rounded-2xl focus:outline-none focus:ring-4 focus:ring-purple-500/20 transition-all font-bold text-white shadow-inner"
                                    value={userData.apellido_1}
                                    onChange={(e) => setUserData({ ...userData, apellido_1: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="col-span-1 space-y-1">
                                <label className="text-[8px] uppercase font-black tracking-[0.3em] text-white/40 pl-2">Apellido 2</label>
                                <input
                                    className="w-full bg-white/5 border border-white/10 p-3 rounded-2xl focus:outline-none focus:ring-4 focus:ring-purple-500/20 transition-all font-bold text-white shadow-inner"
                                    value={userData.apellido_2}
                                    onChange={(e) => setUserData({ ...userData, apellido_2: e.target.value })}
                                />
                            </div>
                        </>
                    )}

                    {/* Email - Always full width */}
                    <div className="col-span-2 space-y-1">
                        <label className="text-[8px] uppercase font-black tracking-[0.3em] text-white/40 pl-2">Email</label>
                        <input
                            type="email"
                            className="w-full bg-white/5 border border-white/10 p-3 rounded-2xl focus:outline-none focus:ring-4 focus:ring-purple-500/20 transition-all font-bold text-white shadow-inner"
                            value={userData.email}
                            onChange={(e) => setUserData({ ...userData, email: e.target.value })}
                            required
                        />
                    </div>

                    {/* City */}
                    {reg.city !== false && (
                        <div className={`${getColSpan('city', 'country')} space-y-1`}>
                            <label className="text-[8px] uppercase font-black tracking-[0.3em] text-white/40 pl-2">Ciudad</label>
                            <input
                                className="w-full bg-white/5 border border-white/10 p-3 rounded-2xl focus:outline-none focus:ring-4 focus:ring-purple-500/20 transition-all font-bold text-white shadow-inner"
                                value={userData.city}
                                onChange={(e) => setUserData({ ...userData, city: e.target.value })}
                                required
                            />
                        </div>
                    )}

                    {/* Country */}
                    {reg.country !== false && (
                        <div className={`${getColSpan('country', 'city')} space-y-1`}>
                            <label className="text-[8px] uppercase font-black tracking-[0.3em] text-white/40 pl-2">País</label>
                            <input
                                className="w-full bg-white/5 border border-white/10 p-3 rounded-2xl focus:outline-none focus:ring-4 focus:ring-purple-500/20 transition-all font-bold text-white shadow-inner"
                                value={userData.country}
                                onChange={(e) => setUserData({ ...userData, country: e.target.value })}
                                required
                            />
                        </div>
                    )}

                    {/* Representative */}
                    {reg.representative !== false && (
                        <div className="col-span-2 space-y-1">
                            <label className="text-[8px] uppercase font-black tracking-[0.3em] text-white/40 pl-2">Representante que lo visita</label>
                            <select
                                className="w-full bg-white/5 border border-white/10 p-3 rounded-2xl focus:outline-none focus:ring-4 focus:ring-purple-500/20 transition-all font-bold text-white shadow-inner appearance-none"
                                value={userData.representative}
                                onChange={(e) => setUserData({ ...userData, representative: e.target.value })}
                                required
                            >
                                <option value="" disabled className="text-gray-900 bg-white">Seleccione un representante</option>
                                {recomendados.map(rec => (
                                    <option key={rec.id} value={rec.nombre} className="text-gray-900 bg-white">
                                        {rec.nombre}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {!(reg.signature_by_date && !eventStarted) && (
                        <div className="col-span-2 space-y-4 pt-4 border-t border-white/10 mt-4">
                            <label className="flex items-start gap-3 cursor-pointer group">
                                <div className="relative flex items-center justify-center mt-1">
                                    <input
                                        type="checkbox"
                                        className="peer sr-only"
                                        checked={signatureAccepted}
                                        onChange={(e) => {
                                            setSignatureAccepted(e.target.checked);
                                            if (!e.target.checked) setUserData({ ...userData, signature: null });
                                        }}
                                    />
                                    <div className="w-5 h-5 rounded-md border-2 border-purple-500/50 bg-white/5 peer-checked:bg-purple-600 peer-checked:border-purple-600 transition-all flex items-center justify-center group-hover:border-purple-400 shadow-inner">
                                        <svg className={`w-3 h-3 text-white transform transition-transform ${signatureAccepted ? 'scale-100' : 'scale-0'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                </div>
                                <span className="text-[10px] sm:text-xs text-white/70 font-medium leading-snug select-none group-hover:text-white transition-colors">
                                    Con mi firma, autorizo el uso de los datos básicos personales de la lista para el control de asistencia en el evento. Estos datos serán tratados de acuerdo con las regulaciones de privacidad y solo se utilizarán con el propósito de gestionar la asistencia al evento.
                                </span>
                            </label>

                            {signatureAccepted && (
                                <div className="animate-fadeIn w-full">
                                    <label className="block text-[10px] uppercase font-black tracking-[0.3em] text-white/40 mb-2 text-center">Dibuja tu firma aquí</label>
                                    <SignaturePad onSignatureChange={(sig) => setUserData({ ...userData, signature: sig })} />
                                </div>
                            )}
                        </div>
                    )}

                        {reg.terms_accepted && (
                            <div className="col-span-2 space-y-4 pt-4 border-t border-white/10 mt-4">
                                <label className="flex items-start gap-3 cursor-pointer group">
                                    <div className="relative flex items-center justify-center mt-1">
                                        <input
                                            type="checkbox"
                                            className="peer sr-only"
                                            checked={termsAccepted}
                                            onChange={(e) => setTermsAccepted(e.target.checked)}
                                        />
                                        <div className="w-5 h-5 rounded-md border-2 border-purple-500/50 bg-white/5 peer-checked:bg-purple-600 peer-checked:border-purple-600 transition-all flex items-center justify-center group-hover:border-purple-400 shadow-inner">
                                            <svg className={`w-3 h-3 text-white transform transition-transform ${termsAccepted ? 'scale-100' : 'scale-0'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                    </div>
                                    <span className="text-[10px] sm:text-xs text-white/70 font-medium leading-snug select-none group-hover:text-white transition-colors">
                                        Acepto los{' '}
                                        <a 
                                            href={reg.terms_url || '#'} 
                                            target="_blank" 
                                            rel="noopener noreferrer" 
                                            className="text-purple-400 hover:text-purple-300 underline underline-offset-2"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            términos y condiciones
                                        </a>
                                        {' '}y políticas de privacidad.
                                    </span>
                                </label>
                            </div>
                        )}

                        <div className="col-span-2 pt-4">
                            {error && (
                                <div className="bg-red-500/20 text-red-200 p-3 rounded-xl text-[9px] font-black uppercase tracking-widest border border-red-500/30 text-center mb-4">
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={isLoading || (signatureAccepted && !userData.signature) || (reg.terms_accepted && !termsAccepted)}
                            className="w-full bg-white text-[#1c0042] py-4 rounded-3xl font-black uppercase tracking-widest text-[11px] shadow-2xl transition-all transform hover:scale-[1.02] active:scale-95 disabled:opacity-50"
                        >
                            {isLoading ? 'PROCESANDO...' : 'FINALIZAR REGISTRO'}
                        </button>

                        <div className="text-center pt-4">
                            <Link to="/login" className="text-[10px] font-black text-white/40 uppercase tracking-widest hover:text-white transition-colors">
                                ← Volver al Ingreso
                            </Link>
                        </div>
                    </div>
                </form>
            </div>
            <footer className="w-full p-6 flex justify-center items-center z-40 relative">
                <img
                    src="/assets/texto_footer.png"
                    alt="Información Legal"
                    className="h-auto w-[90%] max-w-[600px] opacity-80"
                />
            </footer>
        </div>
    );
};

export default Register;
